import { WorkExperienceCompany, WorkExperienceRole, WorkExperienceBullet } from '@applicator/shared'
import { useState, useEffect, useCallback, useRef } from 'react'
import React from 'react'

import { cascadeCompanyIncluded } from '../../utils/workExperienceInclusion'

import { useWorkExperienceRoleHandlers } from './useWorkExperienceRoleHandlers'

export function useWorkExperienceHandlers(
  companies: WorkExperienceCompany[],
  onChange: (data: WorkExperienceCompany[]) => void,
  onAddCompanyRef?: (fn: () => void) => void,
  onAddRoleRef?: (fn: () => void) => void
) {
  const [editingCompanyId, setEditingCompanyId] = useState<string | null>(null)
  const [editingCompanyDescId, setEditingCompanyDescId] = useState<string | null>(null)
  const [editingRoleId, setEditingRoleId] = useState<string | null>(null)
  const [editingRoleLocationId, setEditingRoleLocationId] = useState<string | null>(null)
  const [editingBulletId, setEditingBulletId] = useState<string | null>(null)
  const [editingBulletText, setEditingBulletText] = useState('')
  const bulletTextareaRef = useRef<HTMLTextAreaElement | null>(null)
  const companyDescTextareaRef = useRef<HTMLTextAreaElement | null>(null)
  const [datePickerState, setDatePickerState] = useState<{
    companyId: string
    roleId: string
    field: 'startDate' | 'endDate'
    position: { top: number; left: number; width: number }
    triggerKey: string
  } | null>(null)
  const datePickerRef = useRef<HTMLDivElement>(null)
  const dateFieldRefs = useRef<{ [key: string]: HTMLElement | null }>({})
  const [editingCompanyName, setEditingCompanyName] = useState('')
  const [editingCompanyDesc, setEditingCompanyDesc] = useState('')
  const [editingRolePosition, setEditingRolePosition] = useState('')
  const [editingRoleLocation, setEditingRoleLocation] = useState('')
  const [editingRoleStartDate, setEditingRoleStartDate] = useState('')
  const [editingRoleEndDate, setEditingRoleEndDate] = useState('')
  const [openTypeDropdown, setOpenTypeDropdown] = useState<{ companyId: string; roleId: string } | null>(null)
  const typeDropdownRefs = useRef<{ [key: string]: HTMLElement | null }>({})
  const [draggedBulletId, setDraggedBulletId] = useState<string | null>(null)
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null)
  const [hoveredBulletId, setHoveredBulletId] = useState<string | null>(null)
  const [hoveredRoleId, setHoveredRoleId] = useState<string | null>(null)
  const [hoveredCompanyId, setHoveredCompanyId] = useState<string | null>(null)
  const [hoveredCompanyDescId, setHoveredCompanyDescId] = useState<string | null>(null)

  const handleAddCompany = useCallback(() => {
    const newCompany: WorkExperienceCompany = {
      id: `company-${Date.now()}`,
      name: '',
      description: '',
      included: true,
      roles: [],
    }
    onChange([...companies, newCompany])
    setEditingCompanyId(newCompany.id)
    setEditingCompanyName('')
    setEditingCompanyDesc('')
    // Return company ID for scrolling
    return newCompany.id
  }, [companies, onChange])

  useEffect(() => {
    if (onAddCompanyRef) {
      onAddCompanyRef(handleAddCompany)
      return () => {
        onAddCompanyRef(() => {})
      }
    }
  }, [handleAddCompany, onAddCompanyRef])

  // Helper function to create new role
  const createNewRole = (): WorkExperienceRole => {
    return {
      id: `role-${Date.now()}`,
      position: '',
      location: '',
      employmentType: 'Full-time',
      employmentTypeIncluded: true,
      startDate: '',
      endDate: '',
      included: true,
      bulletPoints: [],
    }
  }

  // Helper function to add role to company
  const addRoleToCompany = (company: WorkExperienceCompany, newRole: WorkExperienceRole): WorkExperienceCompany => {
    return {
      ...company,
      roles: [...(company.roles || []), newRole],
    }
  }

  const handleAddRole = useCallback((companyId: string) => {
    const newRole = createNewRole()
    const newRoleId = newRole.id
    const updated = companies.map((company) => {
      if (company.id === companyId) {
        return addRoleToCompany(company, newRole)
      }
      return company
    })
    onChange(updated)
    setTimeout(() => {
      setEditingRoleId(newRoleId)
      setEditingRolePosition('')
    }, 0)
  }, [companies, onChange])

  const handleAddRoleToFirst = useCallback(() => {
    if (companies.length === 0) {
      handleAddCompany()
    } else {
      handleAddRole(companies[0].id)
    }
  }, [companies, handleAddCompany, handleAddRole])

  useEffect(() => {
    if (onAddRoleRef) {
      onAddRoleRef(handleAddRoleToFirst)
    }
  }, [handleAddRoleToFirst, onAddRoleRef])

  // Helper function to create new bullet
  const createNewBullet = (bulletId: string, included: boolean): WorkExperienceBullet => {
    return {
      id: bulletId,
      text: '',
      included,
    }
  }

  // Helper function to add bullet to role
  const addBulletToRole = (role: WorkExperienceRole, newBullet: WorkExperienceBullet): WorkExperienceRole => {
    return {
      ...role,
      bulletPoints: [...(role.bulletPoints || []), newBullet],
    }
  }

  // Helper function to update role in company with new bullet
  const updateRoleWithNewBullet = (company: WorkExperienceCompany, roleId: string, newBullet: WorkExperienceBullet): WorkExperienceCompany => {
    return {
      ...company,
      roles: (company.roles || []).map((role) =>
        role.id === roleId ? addBulletToRole(role, newBullet) : role
      ),
    }
  }

  const handleAddBullet = (companyId: string, roleId: string) => {
    const newBulletId = `bullet-${Date.now()}`
    const company = companies.find((c) => c.id === companyId)
    const role = company?.roles?.find((r) => r.id === roleId)
    const bulletIncluded =
      (company?.included ?? true) && (role?.positionIncluded ?? true) && (role?.included ?? true)
    const newBullet = createNewBullet(newBulletId, bulletIncluded)
    
    const updated = companies.map((company) =>
      company.id === companyId ? updateRoleWithNewBullet(company, roleId, newBullet) : company
    )
    onChange(updated)
    setEditingBulletId(newBulletId)
    setEditingBulletText('')
  }

  // Helper function to update bullet points when company is toggled
  const updateBulletPointsForCompanyToggle = (bulletPoints: WorkExperienceBullet[], newIncluded: boolean): WorkExperienceBullet[] => {
    return bulletPoints.map((bullet) => ({
      ...bullet,
      included: newIncluded ? bullet.included : false,
    }))
  }

  // Helper function to get role included state
  const getRoleIncludedState = (role: WorkExperienceRole, newIncluded: boolean): boolean => {
    return newIncluded ? role.included : false
  }

  // Helper function to get role position included state
  const getRolePositionIncludedState = (role: WorkExperienceRole, newIncluded: boolean): boolean => {
    return newIncluded ? (role.positionIncluded ?? true) : false
  }

  // Helper function to get role location included state
  const getRoleLocationIncludedState = (role: WorkExperienceRole, newIncluded: boolean): boolean => {
    return newIncluded ? (role.locationIncluded ?? true) : false
  }

  // Helper function to get role bullet points
  const getRoleBulletPoints = (role: WorkExperienceRole): WorkExperienceBullet[] => {
    return role.bulletPoints || []
  }

  // Helper function to build updated role fields
  const buildUpdatedRoleFields = (role: WorkExperienceRole, newIncluded: boolean): Partial<WorkExperienceRole> => {
    return {
      included: getRoleIncludedState(role, newIncluded),
      positionIncluded: getRolePositionIncludedState(role, newIncluded),
      locationIncluded: getRoleLocationIncludedState(role, newIncluded),
      bulletPoints: updateBulletPointsForCompanyToggle(getRoleBulletPoints(role), newIncluded),
    }
  }

  // Helper function to update role when company is toggled
  const updateRoleForCompanyToggle = (role: WorkExperienceRole, newIncluded: boolean): WorkExperienceRole => {
    return {
      ...role,
      ...buildUpdatedRoleFields(role, newIncluded),
    }
  }

  // Helper function to update company when toggled
  const updateCompanyWhenToggled = (company: WorkExperienceCompany, newIncluded: boolean): WorkExperienceCompany => {
    return {
      ...company,
      included: newIncluded,
      nameIncluded: newIncluded ? (company.nameIncluded ?? true) : false,
      descriptionIncluded: newIncluded ? (company.descriptionIncluded ?? true) : false,
      roles: (company.roles || []).map((role) => updateRoleForCompanyToggle(role, newIncluded)),
    }
  }

  const handleToggleCompany = (companyId: string) => {
    const updated = companies.map((company) => {
      if (company.id !== companyId) return company
      const nextIncluded = !company.included
      if (!nextIncluded) {
        return cascadeCompanyIncluded(company, false)
      }
      return updateCompanyWhenToggled(company, true)
    })
    onChange(updated)
  }

  const handleToggleCompanyDescription = (companyId: string) => {
    const updated = companies.map((company) =>
      company.id === companyId ? { ...company, descriptionIncluded: !(company.descriptionIncluded ?? true) } : company
    )
    onChange(updated)
  }

  const employmentTypes = ['Full-time', 'Part-time', 'Internship', 'Teaching', 'Board', 'Contractor', 'Freelancer']

  const setEditingRoleDate = useCallback((field: 'startDate' | 'endDate', value: string) => {
    if (field === 'startDate') {
      setEditingRoleStartDate(value)
    } else {
      setEditingRoleEndDate(value)
    }
  }, [])

  const handleDeleteCompany = (companyId: string) => {
    onChange(companies.filter((c) => c.id !== companyId))
  }

  const handleUpdateCompany = (companyId: string, field: 'name' | 'description', value: string) => {
    const updated = companies.map((company) =>
      company.id === companyId ? { ...company, [field]: value } : company
    )
    onChange(updated)
  }

  const handleStartEditCompany = (company: WorkExperienceCompany) => {
    setEditingCompanyId(company.id)
    setEditingCompanyName(company.name)
  }

  const handleSaveCompany = (companyId: string) => {
    handleUpdateCompany(companyId, 'name', editingCompanyName)
    setEditingCompanyId(null)
  }

  const handleStartEditCompanyDesc = (company: WorkExperienceCompany) => {
    setEditingCompanyDescId(company.id)
    setEditingCompanyDesc(company.description || '')
  }

  const handleSaveCompanyDesc = (companyId: string) => {
    handleUpdateCompany(companyId, 'description', editingCompanyDesc)
    setEditingCompanyDescId(null)
  }

  const adjustTextareaHeight = (textarea: HTMLTextAreaElement) => {
    textarea.style.height = 'auto'
    textarea.style.height = `${textarea.scrollHeight}px`
  }

  const handleCompanyDescChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setEditingCompanyDesc(e.target.value)
    adjustTextareaHeight(e.target)
  }

  // Use role handlers hook
  const roleHandlers = useWorkExperienceRoleHandlers(
    companies,
    onChange,
    editingRolePosition,
    editingRoleLocation,
    editingBulletText,
    editingBulletId,
    datePickerState,
    openTypeDropdown,
    dateFieldRefs,
    typeDropdownRefs,
    setEditingRoleId,
    setEditingRoleLocationId,
    setEditingBulletId,
    setEditingBulletText,
    setEditingRolePosition,
    setEditingRoleLocation,
    setDatePickerState,
    setOpenTypeDropdown,
    setEditingRoleDate,
    datePickerRef,
  )

  const handleBulletTextChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setEditingBulletText(e.target.value)
    adjustTextareaHeight(e.target)
  }

  useEffect(() => {
    if (editingBulletId && bulletTextareaRef.current) {
      adjustTextareaHeight(bulletTextareaRef.current)
    }
  }, [editingBulletId])

  useEffect(() => {
    if (editingCompanyDescId && companyDescTextareaRef.current) {
      adjustTextareaHeight(companyDescTextareaRef.current)
    }
  }, [editingCompanyDescId])

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (datePickerState && datePickerRef.current && !datePickerRef.current.contains(event.target as Node)) {
        const target = event.target as HTMLElement
        const triggerElement = dateFieldRefs.current[datePickerState.triggerKey]
        if (triggerElement && !triggerElement.contains(target)) {
          setDatePickerState(null)
        }
      }
    }

    if (datePickerState) {
      document.addEventListener('mousedown', handleClickOutside)
      return () => {
        document.removeEventListener('mousedown', handleClickOutside)
      }
    }
  }, [datePickerState])

  return {
    // State
    editingCompanyId,
    editingCompanyDescId,
    editingRoleId,
    editingRoleLocationId,
    editingBulletId,
    editingBulletText,
    bulletTextareaRef,
    companyDescTextareaRef,
    datePickerState,
    datePickerRef,
    dateFieldRefs,
    editingCompanyName,
    editingCompanyDesc,
    editingRolePosition,
    editingRoleLocation,
    editingRoleStartDate,
    editingRoleEndDate,
    openTypeDropdown,
    typeDropdownRefs,
    draggedBulletId,
    dragOverIndex,
    hoveredBulletId,
    hoveredRoleId,
    hoveredCompanyId,
    hoveredCompanyDescId,
    employmentTypes,
    // Setters
    setEditingCompanyId,
    setEditingCompanyDescId,
    setEditingRoleId,
    setEditingRoleLocationId,
    setEditingBulletId,
    setEditingBulletText,
    setEditingCompanyName,
    setEditingCompanyDesc,
    setEditingRolePosition,
    setEditingRoleLocation,
    setDatePickerState,
    setOpenTypeDropdown,
    setDraggedBulletId,
    setDragOverIndex,
    setHoveredBulletId,
    setHoveredRoleId,
    setHoveredCompanyId,
    setHoveredCompanyDescId,
    // Handlers
    handleAddCompany,
    handleAddRole,
    handleAddBullet,
    handleToggleCompany,
    handleToggleCompanyDescription,
    handleDeleteCompany,
    handleUpdateCompany,
    handleStartEditCompany,
    handleSaveCompany,
    handleStartEditCompanyDesc,
    handleSaveCompanyDesc,
    adjustTextareaHeight,
    handleCompanyDescChange,
    handleBulletTextChange,
    ...roleHandlers,
  }
}
